import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Stack, Typography } from '@mui/material';
import { profileSchema } from 'common/schema';
import React from 'react';
import { useForm } from 'react-hook-form';
import FormProvider from 'common/components/form-provider';
import BaseTextField from 'common/components/base-text-field';
import { colors, styleInput } from 'common/constants';
import avatarDefault from 'assets/images/icons/avatar-default.svg';
import { createStyleSheet, handleError } from 'utils/helpers';
import RequiredLabel from 'common/components/required-label';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BaseImageUpload from 'common/components/base-img-upload';
import { common } from '@mui/material/colors';
import { getUserInfo, updateUserInfo } from 'services/user.service';
import { toast } from 'react-toastify';
import { ReactComponent as SyncIcon } from 'assets/images/icons/sync.svg';
import CloseIcon from '@mui/icons-material/Close';
import { setUser } from 'slices/userSlice';
import { useDispatch } from 'react-redux';

interface ProfileProps {
    edit?: boolean;
}
const Profile: React.FC<ProfileProps> = ({ edit }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const methods = useForm({
        resolver: yupResolver(profileSchema),
        defaultValues: {
            avatar: '',
            username: '',
            email: '',
            uid: '',
        },
    });
    const {
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = methods;
    const dispatch = useDispatch();
    const onSubmit = handleSubmit(async (data) => {
        const formData = new FormData();
        const payload = {
            username: data.username,
            avatar: data?.avatar as File,
        };

        formData.append('admin[username]', payload.username);
        if (payload.avatar instanceof File) {
            formData.append('admin[avatar]', payload.avatar);
        }
        try {
            const response = await updateUserInfo(formData);
            if (response.success) {
                toast.success(response.message);
                dispatch(setUser(response.data));
            }
        } catch (error) {
            handleError(error);
        }
    });

    React.useEffect(() => {
        const getUserDetail = async () => {
            try {
                const response = await getUserInfo();
                if (response.success) {
                    reset({
                        avatar: response.data.attributes.avatar,
                        username: response.data.attributes.username,
                        email: response.data.attributes.email,
                        uid: response.data.attributes.uid,
                    });
                    dispatch(setUser(response.data));
                }
            } catch (error) {
                handleError(error);
            }
        };
        getUserDetail();
    }, [reset, edit, dispatch]);
    return (
        <Box
            sx={{
                marginTop: '2rem',
            }}
        >
            <Typography variant="h4" sx={styles.title}>
                {!edit
                    ? t('common.user.infomation')
                    : 'Chỉnh sửa thông tin cá nhân'}
            </Typography>
            <FormProvider methods={methods} onSubmit={onSubmit}>
                <Box sx={{ marginBottom: '24px', fontWeight: '600' }}>
                    {t('common.avatar')}
                </Box>
                <Box display={'flex'} gap={'60px'}>
                    <Box>
                        <BaseImageUpload
                            name="avatar"
                            disabled={!edit}
                            multiple={false}
                            sx={{
                                ...styles.avatar,
                                backgroundImage: watch().avatar
                                    ? `url(${watch().avatar})`
                                    : `url(${avatarDefault})`,
                                border: watch().avatar
                                    ? 'none'
                                    : `2px dotted ${colors.main}`,
                                borderRadius: '50%',
                            }}
                        />

                        {errors.avatar && (
                            <Box
                                sx={{
                                    fontSize: '14px',
                                    marginLeft: 0,
                                    color: `${colors.textError} !important`,
                                    fontWeight: '400',
                                }}
                            >
                                {errors.avatar.message}
                            </Box>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '37px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '37px',
                            }}
                        >
                            <Stack spacing={'7px'}>
                                <RequiredLabel
                                    label={t('common.user.id')}
                                    required={false}
                                />
                                <BaseTextField
                                    autoComplete="off"
                                    name="uid"
                                    fullWidth
                                    size="small"
                                    sx={styles.input}
                                    disabled
                                />
                            </Stack>
                            <Stack spacing={'7px'}>
                                <RequiredLabel
                                    label={t('common.user.name')}
                                    required={false}
                                />
                                <BaseTextField
                                    autoComplete="off"
                                    name="username"
                                    fullWidth
                                    size="small"
                                    sx={styles.input}
                                    disabled={!edit}
                                    inputProps={{ maxLength: 55 }}
                                />
                            </Stack>
                        </Box>

                        <Box>
                            <Stack spacing={'7px'}>
                                <RequiredLabel label="Email" required={false} />
                                <BaseTextField
                                    autoComplete="off"
                                    name="email"
                                    fullWidth
                                    size="small"
                                    sx={styles.input}
                                    disabled
                                />
                            </Stack>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '37px',
                            }}
                        >
                            <Stack spacing={'7px'}>
                                {!edit ? (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        sx={styles.btnEdit}
                                        onClick={() =>
                                            navigate('/admin/profile/edit')
                                        }
                                    >
                                        {t('common.edit')}
                                    </Button>
                                ) : (
                                    <Stack
                                        display={'flex'}
                                        flexDirection={'row'}
                                        gap={1}
                                    >
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            sx={styles.btnEdit}
                                            startIcon={<SyncIcon />}
                                        >
                                            {t('common.update')}
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => {
                                                navigate('/admin/profile');
                                                reset();
                                            }}
                                            startIcon={<CloseIcon />}
                                            sx={{
                                                ...styles.btnEdit,
                                                width: '155px',
                                                backgroundColor: common.white,
                                                border: `2px solid ${colors.main}`,
                                                ':hover': {
                                                    backgroundColor:
                                                        common.white,
                                                    border: `2px solid ${colors.main}`,
                                                },
                                            }}
                                        >
                                            {t('common.close')}
                                        </Button>
                                    </Stack>
                                )}
                            </Stack>
                        </Box>
                    </Box>
                </Box>
            </FormProvider>
        </Box>
    );
};

const styles = createStyleSheet({
    input: {
        '& .MuiInputBase-root': {
            ...styleInput['& .MuiInputBase-root'],
            width: '400px',
            height: '60px',
            fontSize: '22px',
        },
    },
    title: {
        fontSize: '40px',
        fontWeight: 700,
        color: colors.main,
        marginBottom: '27px',
    },
    avatar: {
        width: '348px',
        height: '348px',
        backgroundImage: `url(${avatarDefault})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'bottom',
        borderRadius: '17px',
        border: `2px dotted ${colors.main}`,
    },
    btnEdit: {
        width: '230px',
        height: '60px',
        fontSize: '20px',
        lineHeight: '24px',
        fontWeight: '700',
        borderRadius: '40px',
        textTransform: 'none',
    },
});

export default Profile;
